/**
 * Ability and ultimate definitions (DECISIONS.md #18): compositions of data-driven primitives.
 * An ability is a list of steps at time offsets; each step runs one primitive once. No ability is
 * bespoke code; the `custom` primitive exists only as an escape hatch and has no registry entries.
 */
import { z } from 'zod';
const finite = z.number().finite();
const positive = finite.positive();
const nonNegative = finite.nonnegative();
const fraction = finite.min(0).max(1);
/** Longest invulnerability any ability may grant (BUILD_PLAN section 6 guardrail). */
export const MAX_INVULNERABLE_SECONDS = 1.5;
export const EffectFlagValueSchema = z.enum([
    'noBoost',
    'noJump',
    'noAbilities',
    'noShieldRegen',
    'noWeapons',
    'immobile',
    'noTurn',
    'ccImmune',
    'cloaked',
    'revealed',
    'invulnerable',
    'heatLock',
    'noUltCharge',
    'stunned',
]);
/** Stat multipliers and flags a buff, debuff or stance applies. */
export const StatModsSchema = z
    .object({
    speedScale: positive.optional(),
    turnScale: positive.optional(),
    damageScale: positive.optional(),
    damageTakenScale: positive.optional(),
    fireRateScale: positive.optional(),
    heatScale: nonNegative.optional(),
    dissipationScale: nonNegative.optional(),
    spreadScale: nonNegative.optional(),
    armorBonus: finite.min(-0.8).max(0.8).optional(),
    cooldownScale: positive.optional(),
    jetThrustScale: nonNegative.optional(),
    jetCostScale: nonNegative.optional(),
    flags: z.array(EffectFlagValueSchema).optional(),
})
    .strict();
export const TargetingSchema = z.enum(['self', 'enemy', 'radius']);
const DashPrimitive = z
    .object({
    type: z.literal('dash'),
    /** `run` moves over `seconds`; `blink` teleports instantly (walls still stop it). */
    mode: z.enum(['run', 'blink']),
    direction: z.enum(['move', 'aim', 'forward', 'back', 'toTarget']),
    distance: positive,
    seconds: positive.default(0.3),
    invulnerableSeconds: nonNegative.max(MAX_INVULNERABLE_SECONDS).default(0),
    damage: nonNegative.default(0),
    knockback: nonNegative.default(0),
    stopOnHit: z.boolean().default(false),
    ccImmune: z.boolean().default(false),
})
    .strict();
const CloakPrimitive = z
    .object({
    type: z.literal('cloak'),
    seconds: positive,
    revealDistance: positive.default(6),
    breakOnFire: z.boolean().default(true),
})
    .strict();
const DeployPrimitive = z
    .object({
    type: z.literal('deploy'),
    kind: z.enum(['barrier', 'turret', 'drone', 'field', 'marker']),
    seconds: positive,
    hp: nonNegative.default(0),
    /** Metres in front of the mech, or at the aimed ground point when `atTarget`. */
    forward: nonNegative.default(3),
    atTarget: z.boolean().default(false),
    count: z.number().int().positive().default(1),
    halfExtents: z.object({ x: positive, y: positive, z: positive }).strict().optional(),
    weapon: z.string().optional(),
    range: positive.optional(),
    hpPerSecond: positive.optional(),
    pulse: z
        .object({
        interval: positive,
        damage: positive,
        radius: positive,
        slowScale: positive.optional(),
        slowSeconds: positive.optional(),
    })
        .strict()
        .optional(),
    detonate: z
        .object({ damage: positive, radius: positive, knockUp: nonNegative.default(0) })
        .strict()
        .optional(),
})
    .strict();
const BuffPrimitive = z
    .object({
    type: z.literal('buff'),
    /** Label for events and HUD icons. */
    kind: z.string().min(1),
    target: TargetingSchema,
    radius: positive.optional(),
    seconds: positive,
    mods: StatModsSchema,
    lineOfSight: z.boolean().default(false),
})
    .strict();
const AoePrimitive = z
    .object({
    type: z.literal('aoe'),
    shape: z.enum(['sphere', 'cone', 'ring']),
    origin: z.enum(['self', 'target', 'aimGround']).default('self'),
    radius: positive,
    innerRadius: nonNegative.default(0),
    /** Cone half-angle, degrees. */
    angleDeg: positive.default(45),
    damage: nonNegative,
    /** Damage factor at the edge of the radius. */
    minFactor: fraction.default(0.5),
    lineOfSight: z.boolean().default(true),
    knockback: nonNegative.default(0),
    knockUp: nonNegative.default(0),
    /** Kill outright below this fraction of max HP (0 disables). */
    executeBelow: fraction.default(0),
    selfDamage: z.boolean().default(false),
    /** Status effect applied to every enemy hit. */
    effect: z
        .object({ kind: z.string().min(1), seconds: positive, mods: StatModsSchema })
        .strict()
        .optional(),
    /** Damage over time applied to every enemy hit (burn). */
    dot: z.object({ damagePerSecond: positive, seconds: positive }).strict().optional(),
})
    .strict();
const BurstPrimitive = z
    .object({
    type: z.literal('burst'),
    /** A projectile weapon used as ammunition. */
    weapon: z.string().min(1),
    count: z.number().int().positive(),
    intervalSeconds: nonNegative,
    /** `aim` along the aim; `swarmUp` launches upward with homing; `mortar` lobs at the aimed ground point. */
    pattern: z.enum(['aim', 'swarmUp', 'mortar']).default('aim'),
    spreadDeg: nonNegative.default(0),
    /** Mortar shells land within this radius of the target point, metres. */
    scatterMeters: nonNegative.default(0),
})
    .strict();
const BeamPrimitive = z
    .object({
    type: z.literal('beam'),
    /** A beam weapon definition fired for the duration regardless of the trigger. */
    weapon: z.string().min(1),
    seconds: positive,
    /** Steal HP and energy from the target while it connects (Leviathan's siphon). */
    siphon: z
        .object({ hpPerSecond: nonNegative.default(0), energyPerSecond: nonNegative.default(0) })
        .strict()
        .optional(),
})
    .strict();
const CcPrimitive = z
    .object({
    type: z.literal('cc'),
    kind: z.enum(['stun', 'root', 'slow', 'emp', 'knockback', 'knockUp', 'pull', 'heatLock']),
    target: z.enum(['enemy', 'radius']),
    radius: positive.optional(),
    seconds: positive.default(0.1),
    /** Slow factor (0..1 of speed kept), or knockback / pull / knock-up speed in m/s. */
    strength: nonNegative.default(0),
    lineOfSight: z.boolean().default(true),
})
    .strict();
const HealPrimitive = z
    .object({
    type: z.literal('heal'),
    hp: nonNegative.default(0),
    shield: nonNegative.default(0),
    /** Heat removed (positive cools). */
    heat: nonNegative.default(0),
    energy: nonNegative.default(0),
    /** 0 applies instantly; otherwise the amounts are spread over the seconds. */
    seconds: nonNegative.default(0),
})
    .strict();
const StancePrimitive = z
    .object({
    type: z.literal('stance'),
    kind: z.string().min(1),
    mode: z.enum(['toggle', 'timed']),
    seconds: positive.optional(),
    mods: StatModsSchema,
    /** Attacks from within the arc are blocked, projectiles reflected, melee attackers stunned. */
    parry: z.object({ arcDeg: positive, stunSeconds: nonNegative }).strict().optional(),
})
    .strict();
const CustomPrimitive = z
    .object({
    type: z.literal('custom'),
    id: z.string().min(1),
    params: z.record(z.string(), z.unknown()).default({}),
})
    .strict();
export const PrimitiveSchema = z.discriminatedUnion('type', [
    DashPrimitive,
    CloakPrimitive,
    DeployPrimitive,
    BuffPrimitive,
    AoePrimitive,
    BurstPrimitive,
    BeamPrimitive,
    CcPrimitive,
    HealPrimitive,
    StancePrimitive,
    CustomPrimitive,
]);
export const BotHintSchema = z.enum([
    'gapClose',
    'escape',
    'buff',
    'aoe',
    'deploy',
    'cc',
    'heal',
    'stance',
    'burst',
    'beam',
    'cloak',
]);
export const AbilityDefinitionSchema = z
    .object({
    id: z.string().regex(/^[a-z][a-z0-9-]*$/),
    name: z.string().min(1),
    description: z.string().min(1),
    botHint: BotHintSchema,
    energyCost: nonNegative.default(0),
    /** Needs an enemy in line of sight within this range (0 = no requirement). */
    targetRange: nonNegative.default(0),
    steps: z.array(z.object({ at: nonNegative, primitive: PrimitiveSchema }).strict()).min(1),
})
    .strict()
    .superRefine((ability, ctx) => {
    for (const step of ability.steps) {
        const p = step.primitive;
        if (p.type === 'dash' && p.invulnerableSeconds > MAX_INVULNERABLE_SECONDS) {
            ctx.addIssue({ code: 'custom', message: `${ability.id}: invulnerability over 1.5 s` });
        }
        if (p.type === 'buff' &&
            p.mods.flags?.includes('invulnerable') &&
            p.seconds > MAX_INVULNERABLE_SECONDS) {
            ctx.addIssue({ code: 'custom', message: `${ability.id}: invulnerability over 1.5 s` });
        }
        if (p.type === 'stance' && p.mods.flags?.includes('invulnerable')) {
            ctx.addIssue({ code: 'custom', message: `${ability.id}: stances cannot be invulnerable` });
        }
        if ((p.type === 'buff' || p.type === 'cc') &&
            p.target === 'radius' &&
            p.radius === undefined) {
            ctx.addIssue({ code: 'custom', message: `${ability.id}: radius targeting needs a radius` });
        }
    }
});
/** Seconds from activation until the last step starts plus that step's own duration. */
export function abilityDurationSeconds(def) {
    let end = 0;
    for (const step of def.steps) {
        const p = step.primitive;
        let length = 0;
        switch (p.type) {
            case 'dash':
                length = p.mode === 'run' ? p.seconds : 0;
                break;
            case 'cloak':
            case 'deploy':
            case 'buff':
            case 'beam':
            case 'cc':
            case 'heal':
                length = p.seconds;
                break;
            case 'burst':
                length = p.count * p.intervalSeconds;
                break;
            case 'stance':
                length = p.seconds ?? 0;
                break;
            case 'aoe':
            case 'custom':
                length = 0;
                break;
        }
        end = Math.max(end, step.at + length);
    }
    return end;
}
//# sourceMappingURL=ability-schema.js.map